import { Fragment, SetStateAction } from 'react';
import isEqual from 'react-fast-compare';

import { Box, Button, Flex, VStack } from '@oaknorth/component-core-v2';
import { T } from '@oaknorth/util-intl';

import { TableSearchControl } from '../components';

import { TableFiltersProps } from './types';

export function TableFilters<Row extends object, Filters>({
  table,
  initialFilters,
  onChangeFilters,
  filters,
  currentFilters,
  'data-testid': testId,
  searchPlaceholder,
}: TableFiltersProps<Row, Filters>) {
  const handleClearAllFilters = () => {
    onChangeFilters(initialFilters);
    table.resetGlobalFilter();
    table.resetPageIndex();
  };

  const handleFilterChange =
    <T extends keyof Filters>(key: T) =>
    (val: Filters[T]) => {
      onChangeFilters((prev) => ({ ...prev, [key]: val }));
      // Always reset page when filters change
      table.resetPageIndex();
    };

  const handleAllFilterChange = (newFilterOrUpdater: SetStateAction<Filters>) => {
    onChangeFilters(newFilterOrUpdater);
    table.resetPageIndex();
  };
  const hasAnyFilterApplied = !isEqual(currentFilters, initialFilters);

  return (
    <VStack align="stretch" width="full" data-testid={testId}>
      <Flex gap={2} align="center" flexWrap="wrap">
        <Box width="220px">
          <TableSearchControl table={table} size="sm" placeholder={searchPlaceholder} />
        </Box>

        {filters.map((filter) => (
          <Fragment key={filter.id}>
            {'accessorKey' in filter
              ? filter.renderFilterButton(
                  currentFilters[filter.accessorKey],
                  handleFilterChange(filter.accessorKey),
                  currentFilters,
                  handleAllFilterChange
                )
              : filter.renderFilterButton(currentFilters, handleAllFilterChange)}
          </Fragment>
        ))}
      </Flex>
      {hasAnyFilterApplied ? (
        <Flex gap={2} flexWrap="wrap">
          {filters.map((filter) => (
            <Fragment key={filter.id}>
              {'accessorKey' in filter
                ? filter.renderFilterTag(
                    currentFilters[filter.accessorKey],
                    handleFilterChange(filter.accessorKey),
                    currentFilters,
                    handleAllFilterChange
                  )
                : filter.renderFilterTag(currentFilters, handleAllFilterChange)}
            </Fragment>
          ))}
          <Button
            size="sm"
            variant="ghost"
            onClick={handleClearAllFilters}
            data-testid="filters-clearAll"
            height="unset"
          >
            {T`filter.clearAll.label`}
          </Button>
        </Flex>
      ) : null}
    </VStack>
  );
}
